import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Textarea from '@amzn/meridian/textarea';
import { Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import FormikRangedButtonGroup from './components/FormikRangedButtonGroup';
import { WeeklySummary } from '@amzn/aet-summaries-kit';
import FeedbackQuestion from './components/FeedbackQuestion';
import YesNoRadioGroup from './components/YesNoRadioGroup';
import YesNoNARadioGroup from './components/YesNoNARadioGroup';
import DataSource from '../../../api';
import Naws from '../../../api/Naws';
import { ManagerFeedbackInput } from '../types';
import useInspireOptions from '../../../hooks/useInspireOptions';
import DataState from '../../../Types/DataState';
import { useToaster } from '../../../Util/AppToaster';
import Box from '@amzn/meridian/box';
import { FEEDBACK_RESPONSE } from './constants';

interface FeedbackFormValues {
  containsFalseOrContradictions: string;
  falseOrContradictionsFeedback: string;
  understandability: number;
  tonality: number;
  willUseInCoaching: string;
  coachingUsabilityFeedback: string;
  additionalFeedback: string;
}

const validationSchema = Yup.object({
  containsFalseOrContradictions: Yup.string().required(),
  falseOrContradictionsFeedback: Yup.string().when(
    'containsFalseOrContradictions',
    {
      is: FEEDBACK_RESPONSE.YES,
      then: Yup.string().required('Please provide details'),
      otherwise: Yup.string(),
    }
  ),
  understandability: Yup.number().min(1).max(5).required(),
  tonality: Yup.number().min(1).max(5).required(),
  willUseInCoaching: Yup.string().required(),
  coachingUsabilityFeedback: Yup.string().when('willUseInCoaching', {
    is: (value: string) =>
      value === FEEDBACK_RESPONSE.NO || value === FEEDBACK_RESPONSE.NA,
    then: Yup.string().required('Please provide explanation'),
    otherwise: Yup.string(),
  }),
  additionalFeedback: Yup.string(),
});

const initialValues: FeedbackFormValues = {
  containsFalseOrContradictions: '',
  falseOrContradictionsFeedback: '',
  understandability: -1,
  tonality: -1,
  willUseInCoaching: '',
  coachingUsabilityFeedback: '',
  additionalFeedback: '',
};

interface ManagerFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  summary: WeeklySummary;
}

const ManagerFeedbackModal = ({
  isOpen = true,
  onClose = () => {},
  onSuccess = () => {},
  summary,
}: ManagerFeedbackModalProps) => {
  const apiNaws: typeof Naws = DataSource.getDataFeed('NAWS');
  const [submissionState, setSubmissionState] = useState(DataState.INITIALIZED);
  const { requesterUsername } = useInspireOptions();

  const { openToast } = useToaster();

  const intl = useIntl();

  const handleSubmit = async (values: FeedbackFormValues) => {
    // normalize feedback
    const normalizedFeedback: ManagerFeedbackInput = {
      containsFalseOrContradictions:
        values.containsFalseOrContradictions === FEEDBACK_RESPONSE.YES,
      falseOrContradictionsFeedback:
        values.containsFalseOrContradictions === FEEDBACK_RESPONSE.YES
          ? values.falseOrContradictionsFeedback
          : undefined,
      understandability: values.understandability,
      tonality: values.tonality,
      willUseInCoaching:
        values.willUseInCoaching === FEEDBACK_RESPONSE.YES
          ? true
          : values.willUseInCoaching === FEEDBACK_RESPONSE.NO
            ? false
            : undefined,
      coachingUsabilityFeedback:
        values.willUseInCoaching === FEEDBACK_RESPONSE.NO ||
        values.willUseInCoaching === FEEDBACK_RESPONSE.NA
          ? values.coachingUsabilityFeedback
          : undefined,
      additionalFeedback: values.additionalFeedback || undefined,
    };

    await apiNaws.submitCSASummariesManagerFeedback(
      {
        alias: requesterUsername,
        csaAlias: summary.alias,
        date: summary.date,
        summaryUUID: summary.uuid,
        feedback: normalizedFeedback,
      },
      () => {},
      setSubmissionState
    );
    // Handle form submission here
    onClose();
  };

  useEffect(() => {
    if (submissionState === DataState.SUCCESS) {
      handleSuccess();
    }

    if (submissionState === DataState.ERROR) {
      handleError();
    }
  }, [submissionState]);

  const handleSuccess = () => {
    openToast({
      children: (
        <FormattedMessage
          id='cs_perfportal_summaries_manager_submit_summaries_feedback_success'
          defaultMessage='<bold>Response received.</bold> Thanks for your input.'
          values={{
            bold: chunks => <b>{chunks}</b>,
          }}
        />
      ),
      alertType: 'success',
    });

    onSuccess();
    onClose();
  };

  const handleError = () => {
    openToast({
      children: (
        <FormattedMessage
          id='cs_perfportal_summaries_manager_submit_summaries_feedback_error'
          defaultMessage='<bold>There was an issue saving your response.</bold> Please try again.'
          values={{
            bold: chunks => <b>{chunks}</b>,
          }}
        />
      ),
      alertType: 'error',
    });
    onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      validateOnMount
    >
      {formik => (
        <Modal
          title={intl.formatMessage(
            {
              id: 'cs_perfportal_summaries_manager_feedback_modal_title',
              defaultMessage: "Give feedback on {alias}'s Weekly highlight",
            },
            { alias: summary.alias }
          )}
          open={isOpen}
          onClose={onClose}
          scrollContainer='modal'
          aria-describedby='modal-description'
          data-testid='manager-feedback-modal'
        >
          <Column
            spacing='500'
            spacingInset='200 450'
            alignmentHorizontal='start'
            alignmentVertical='top'
            maxWidth='32vw'
          >
            {/* Question 1: Does the highlight include any information that seems false or unsupported? */}
            <FeedbackQuestion
              questionNumber={1}
              questionText={
                <FormattedMessage
                  id='cs_perfportal_summaries_manager_feedback_question_1'
                  defaultMessage='Does the highlight include any information that seems false or unsupported?'
                />
              }
              required
            >
              <YesNoRadioGroup
                formik={formik}
                fieldName='containsFalseOrContradictions'
              />
            </FeedbackQuestion>

            {/* Question 1 Conditional: If Yes, show feedback textarea */}
            {formik.values.containsFalseOrContradictions ===
              FEEDBACK_RESPONSE.YES && (
              <Box spacingInset='none' width='100%'>
                <Textarea
                  size='small'
                  value={formik.values.falseOrContradictionsFeedback}
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_summaries_manager_feedback_q1_text_placeholder',
                    defaultMessage:
                      'Please tell us what information seems false or unsupported.',
                  })}
                  disabled={false}
                  width='100%'
                  onChange={e =>
                    formik.setFieldValue('falseOrContradictionsFeedback', e)
                  }
                  data-testid='false-contradictions-feedback-textarea'
                />
              </Box>
            )}

            {/* Question 2: How clear and understandable is the highlight? */}
            <FeedbackQuestion
              questionNumber={2}
              questionText={
                <FormattedMessage
                  id='cs_perfportal_summaries_manager_feedback_question_2_new'
                  defaultMessage='How clear and understandable is the highlight?'
                />
              }
              required
            >
              <FormikRangedButtonGroup
                formik={formik}
                fieldName='understandability'
                min={1}
                max={5}
                minLabel={
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_very_unclear'
                    defaultMessage='Very unclear'
                  />
                }
                maxLabel={
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_very_clear'
                    defaultMessage='Very clear'
                  />
                }
              />
            </FeedbackQuestion>

            {/* Question 3: How would you rate the language and tone */}
            <FeedbackQuestion
              questionNumber={3}
              questionText={
                <FormattedMessage
                  id='cs_perfportal_summaries_manager_feedback_question_3_new'
                  defaultMessage='How would you rate the language and tone of the highlight from very unprofessional and overly critical to very professional and supportive?'
                />
              }
              required
            >
              <FormikRangedButtonGroup
                formik={formik}
                fieldName='tonality'
                min={1}
                max={5}
                minLabel={
                  <div>
                    <FormattedMessage
                      id='cs_perfportal_summaries_manager_feedback_tonality_range_bad'
                      defaultMessage='Very unprofessional & critical'
                    />
                  </div>
                }
                maxLabel={
                  <div>
                    <FormattedMessage
                      id='cs_perfportal_summaries_manager_feedback_tonality_range_good'
                      defaultMessage='Very professional & supportive'
                    />
                  </div>
                }
              />
            </FeedbackQuestion>

            {/* Question 4: Will you use the information in your next coaching session? */}
            <FeedbackQuestion
              questionNumber={4}
              questionText={
                <FormattedMessage
                  id='cs_perfportal_summaries_manager_feedback_question_4_new'
                  defaultMessage='Will you use the information from the highlight in your next coaching session with the associate?'
                />
              }
              required
            >
              <YesNoNARadioGroup
                formik={formik}
                fieldName='willUseInCoaching'
              />
            </FeedbackQuestion>

            {/* Question 4 Conditional: If No or N/A, show feedback textarea */}
            {(formik.values.willUseInCoaching === FEEDBACK_RESPONSE.NO ||
              formik.values.willUseInCoaching === FEEDBACK_RESPONSE.NA) && (
              <Box spacingInset='none' width='100%'>
                <Textarea
                  size='small'
                  value={formik.values.coachingUsabilityFeedback}
                  placeholder={
                    formik.values.willUseInCoaching === FEEDBACK_RESPONSE.NO
                      ? intl.formatMessage({
                          id: 'cs_perfportal_summaries_manager_feedback_q4_placeholder_NO',
                          defaultMessage:
                            'Please tell us why you don’t think you’ll use this information in your next coaching session.',
                        })
                      : intl.formatMessage({
                          id: 'cs_perfportal_summaries_manager_feedback_q4_placeholder_NA',
                          defaultMessage: `Please tell us why this question isn't applicable.`,
                        })
                  }
                  disabled={false}
                  width='100%'
                  onChange={e =>
                    formik.setFieldValue('coachingUsabilityFeedback', e)
                  }
                  data-testid='coaching-feedback-textarea'
                />
              </Box>
            )}

            {/* Additional feedback section - Optional */}
            <Box spacingInset='none' width='100%'>
              <Text>
                <b>
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_additional'
                    defaultMessage='Do you have any other feedback?'
                  />
                </b>
              </Text>
              <Box spacingInset='200 none none'>
                <Text color='secondary' type='b200'>
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_question_8_helper'
                    defaultMessage="Don't include any identifying information, such as phone number or address."
                  />
                </Text>
              </Box>
              <Box spacingInset='300 none none'>
                <Textarea
                  size='small'
                  value={formik.values.additionalFeedback}
                  placeholder={intl.formatMessage({
                    id: 'cs_perfportal_summaries_manager_feedback_placeholder_specific',
                    defaultMessage: 'Please be as specific as possible',
                  })}
                  disabled={false}
                  width='100%'
                  onChange={e => formik.setFieldValue('additionalFeedback', e)}
                  data-testid='additional-feedback-textarea'
                />
              </Box>
            </Box>
          </Column>

          <ModalFooter>
            <Row alignmentHorizontal='end' widths='fit'>
              <Button
                type='secondary'
                size='small'
                onClick={onClose}
                data-testid='manager-feedback-cancel-btn'
              >
                <FormattedMessage
                  id='cs_perfportal_summaries_manager_feedback_cancel'
                  defaultMessage='Cancel'
                />
              </Button>
              <Button
                type='primary'
                size='small'
                onClick={() => formik.handleSubmit()}
                disabled={formik.isSubmitting || !formik.isValid}
                data-testid='manager-feedback-submit-btn'
              >
                {formik.isSubmitting ? (
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_sending'
                    defaultMessage='Sending...'
                  />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_summaries_manager_feedback_send'
                    defaultMessage='Send Feedback'
                  />
                )}
              </Button>
            </Row>
          </ModalFooter>
        </Modal>
      )}
    </Formik>
  );
};

export default ManagerFeedbackModal;
